// Core Modules
import { Injectable, signal, WritableSignal } from '@angular/core';

// Models
import { ToastModel } from '@shared/models/toast.model';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  // Private Members
  private readonly defaults: ToastModel = {
    id: 0,
    message: '',
    dismissible: true,
    durationMs: 4000,
    variant: 'info',
  };
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
  private readonly toastsSignal: WritableSignal<ToastModel[]> = signal<ToastModel[]>([]);
  private nextId = 1;

  // Public Members
  public readonly toasts = this.toastsSignal.asReadonly();

  public dismiss(id?: number): void {
    const activeToasts = this.toastsSignal();
    if (activeToasts.length === 0) {
      return;
    }

    const toastId = id ?? activeToasts[activeToasts.length - 1].id;

    this.clearTimer(toastId);
    this.toastsSignal.update((toasts) => toasts.filter((toast) => toast.id !== toastId));
  }

  public show(config: Partial<ToastModel> & { message: string }): number {
    const toast = this.buildToast(config);

    this.toastsSignal.update((toasts) => [...toasts, toast]);

    if (toast.durationMs > 0) {
      const timer = setTimeout(() => {
        this.dismiss(toast.id);
      }, toast.durationMs);
      this.timers.set(toast.id, timer);
    }

    return toast.id;
  }

  private buildToast(config: Partial<ToastModel> & { message: string }): ToastModel {
    return {
      ...this.defaults,
      ...config,
      id: this.nextId++,
    };
  }

  private clearTimer(id: number): void {
    const timer = this.timers.get(id);

    if (!timer) {
      return;
    }

    clearTimeout(timer);
    this.timers.delete(id);
  }
}
